import React, {useEffect, useRef} from 'react'
import {flowResult} from 'mobx'
import {observer} from 'mobx-react'
import {Breadcrumbs} from '../../components/breadcrumbs'
import {Layout} from '../../components/layout'
import {Box, Container, Text} from '../../vanilla'
import {StatusMap, TObservablePage} from '../../types/commerce'
import {Seo} from '../../components/seo'
import {ChangeAddress} from './partials/change-address'
import {DeliveryBox} from './partials/delivery-box'
import {useBasketStore, useCustomerStore, useDeliveryStore, useRapidDeliveryStore} from '../../store/hooks/useStore'
import useNavigation from '../../hooks/use-navigation'
import useDeliveryBooking from '../../hooks/use-delivery-booking'
import {useCardSlideAnimation} from '../../hooks/use-card-slide-animation'
import {useLocation} from 'react-router-dom'
import {usePageDataTracking} from '../../analytics/page-tracking'
import SlotsErrorMessaging from '../book-delivery/partials/booking-slots/error-messaging'
import { DeliveryBoxSkeleton } from './partials/delivery-box-skeleton'
import { deliveryCardFadeIn } from './partials/delivery-box.css'
import {AvailableDeliveryMethod, DeliveryTypes} from '../../types/delivery'

type DeliveryGridItem =
  | {cardId: string; isSkeleton: true}
  | (AvailableDeliveryMethod & {cardId: string; isSkeleton: false})

const DELIVERY_SKELETONS: DeliveryGridItem[] = [
  {cardId: '1', isSkeleton: true},
  {cardId: '2', isSkeleton: true},
  {cardId: '3', isSkeleton: true},
  {cardId: '4', isSkeleton: true}
]

const getDeliveryCardItems = (
  methods: AvailableDeliveryMethod[],
  idsByDeliveryType: Map<string, number>
): DeliveryGridItem[] => {
  const isSkeletonId = (id: number | undefined): id is number =>
    id !== undefined && Number.isInteger(id) && id >= 1 && id <= DELIVERY_SKELETONS.length
  const reservedIds = new Set(methods.map(({sortOrder}) => sortOrder).filter(isSkeletonId))
  const usedIds = new Set(idsByDeliveryType.values())

  const cards = methods.map<DeliveryGridItem>((method, index) => {
    let id = idsByDeliveryType.get(method.deliveryType)
    if (id === undefined) {
      id = method.sortOrder
      if (!isSkeletonId(id) || usedIds.has(id)) {
        id = index + 1
        while (reservedIds.has(id) || usedIds.has(id)) id++
      }
      idsByDeliveryType.set(method.deliveryType, id)
      usedIds.add(id)
    }
    return {...method, cardId: String(id), isSkeleton: false}
  })

  const unavailableSameDayIndex = methods.findIndex(
    ({deliveryType, available}) => deliveryType === DeliveryTypes.SAMEDAY && !available
  )
  if (unavailableSameDayIndex !== -1) {
    const [sameDayCard] = cards.splice(unavailableSameDayIndex, 1)
    cards.splice(Math.min(3, cards.length), 0, sameDayCard)
  }

  return cards
}

const SelectDelivery: TObservablePage = () => {
  const location = useLocation<{originPath?: string}>()
  const originPath = location?.state?.originPath
  const {basket, deliveryPostalCode, shippingAddress} = useBasketStore()
  const {isRegistered, loading: loadingCustomer, primaryAddress} = useCustomerStore()
  const {isRapidDeliveryAvailableForPostcode, loadRapidDeliveryETA, status: rapidDeliveryStatus} = useRapidDeliveryStore()
  const {setDeliveryAddress} = useDeliveryBooking()
  const {
    availableDeliveryTypes,
    deliverySlotsError,
    isDeliverySlotsLoading,
    isPreBookAvailabilityLoading,
    loadAvailability,
    minDeliveryThreshold
  } = useDeliveryStore()
  const sendPageData = usePageDataTracking()
  const pageType = 'Select Delivery Type'
  const pageCategory = 'Delivery'
  const pageSubCategory = 'Delivery'
  const isLoading = availableDeliveryTypes.some(({loading}) => loading)
  const hasRequestedRapidDeliveryETA = useRef(false)
  const idsByDeliveryType = useRef(new Map<string, number>())
  const lastResolvedDeliveryGridItems = useRef<DeliveryGridItem[]>(DELIVERY_SKELETONS)
  const isDeliveryMethodsLoading =
    isDeliverySlotsLoading ||
    isPreBookAvailabilityLoading ||
    rapidDeliveryStatus === StatusMap.PENDING

  if (!isDeliveryMethodsLoading) {
    lastResolvedDeliveryGridItems.current = getDeliveryCardItems(
      availableDeliveryTypes,
      idsByDeliveryType.current
    )
  }

  const shouldShowSkeletons = isDeliveryMethodsLoading &&
    !lastResolvedDeliveryGridItems.current.some((item) => !item.isSkeleton && item.available)
  const deliveryGridItems: DeliveryGridItem[] = shouldShowSkeletons
    ? DELIVERY_SKELETONS
    : lastResolvedDeliveryGridItems.current
  const {containerRef: deliveryGridRef, cards: deliveryCards} = useCardSlideAnimation(
    deliveryGridItems,
    ({cardId}) => cardId,
    {
      isLoading: isDeliveryMethodsLoading && !shouldShowSkeletons,
      settleDelay: shouldShowSkeletons ? 0 : undefined
    }
  )
  const currentDeliveryMethodIds = new Set(deliveryGridItems.map(({cardId}) => cardId))
  const isDeliveryCardSlidingOut = deliveryCards.some(({id}) => !currentDeliveryMethodIds.has(id))
  const showDeliveryButtonLoaders = isDeliveryMethodsLoading || isDeliveryCardSlidingOut

  useEffect(() => {
    if (isLoading || !availableDeliveryTypes.some(({available}) => available)) return
    sendPageData(pageType, pageCategory, pageSubCategory)
  }, [isLoading, availableDeliveryTypes])

  // Re-direct if customer is not logged in
  const navigate = useNavigation()
  useEffect(() => {
    if (!loadingCustomer && !isRegistered) {
      // Ensure customer returns to this page once logged
      navigate('/login', 'push', {
        directedFrom: '/select-delivery',
        originPath
      })
      return
    }
  }, [loadingCustomer, isRegistered, originPath])

  // Default to the customer's primary address if they haven't already chosen one to deliver to
  useEffect(() => {
    const hasSelectedDeliveryAddress = Boolean(
      shippingAddress?.address1?.trim() && shippingAddress?.postalCode?.trim()
    )
    if (loadingCustomer || !isRegistered || !basket?.basketId || hasSelectedDeliveryAddress || !primaryAddress) return

    setDeliveryAddress(primaryAddress)
  }, [loadingCustomer, isRegistered, basket?.basketId, shippingAddress, primaryAddress])

  useEffect(() => {
    if (!basket?.basketId) return

    loadAvailability().catch((error) => console.error('Error loading delivery availability', error))
  }, [basket?.basketId, deliveryPostalCode, loadAvailability])

  useEffect(() => {
    if (
      hasRequestedRapidDeliveryETA.current ||
      !basket?.basketId ||
      !deliveryPostalCode ||
      !isRapidDeliveryAvailableForPostcode
    ) return

    hasRequestedRapidDeliveryETA.current = true
    flowResult(loadRapidDeliveryETA(deliveryPostalCode)).catch((error) => {
      hasRequestedRapidDeliveryETA.current = false
      console.error('Error loading Rapid Delivery ETA', error)
    })
  }, [basket?.basketId, deliveryPostalCode, isRapidDeliveryAvailableForPostcode, loadRapidDeliveryETA])

  return (
    <Layout>
      <Seo
        title="Choose Your Delivery | Iceland Delivery"
        description="Choose a delivery slot with Iceland and receive your order at a time that suits you."
      />
      <Container paddingX={'16px'} paddingBottom="40px">
        <Box paddingY="12px" data-test-selector="select-delivery-breadcrumbs">
          <Breadcrumbs breadcrumbs={[{id: 'SELECT_DELIVERY', name: 'Choose delivery'}]} />
        </Box>

        <Text
          paddingBottom="40px"
          as="h2"
          variant="heading4"
          lineHeight="taller"
          data-test-selector="select-delivery-heading"
        >
          Choose Your Delivery
        </Text>

        <ChangeAddress />

        <Box position="relative" minHeight="180px" data-test-selector="select-delivery-options">
          {deliverySlotsError ? (
            <SlotsErrorMessaging
              error={deliverySlotsError}
              buttonAction={() => loadAvailability().catch((error) => console.error('Error retrying delivery availability', error))}
            />
          ) : (
            <Box
              ref={deliveryGridRef}
              position="relative"
              display="grid"
              gridTemplateColumns={['1x', '4x']}
              gap="16px"
              marginTop="24px"
              data-test-selector="select-delivery-options-grid"
            >
              {deliveryCards.map(({id, item, isSlidingOut}) => (
                <Box
                  key={id}
                  display="flex"
                  flex="1"
                  width="full"
                  minHeight={['200px', '420px']}
                  backgroundColor={item.isSkeleton || item.available ? 'white' : 'transparent'}
                  padding="16px"
                  flexDirection="column"
                  border="1px"
                  borderColor="gray100"
                  style={
                    item.isSkeleton
                      ? undefined
                      : {
                          backgroundImage: (() => {
                            const backgroundImage = item.backgroundImage
                              ? `url(${JSON.stringify(item.backgroundImage)})`
                              : undefined
                            return item.theme === 'dark' && backgroundImage
                              ? `linear-gradient(rgba(0, 0, 0, 0.56), rgba(0, 0, 0, 0.56)), ${backgroundImage}`
                              : backgroundImage
                          })(),
                          backgroundPosition: 'center',
                          backgroundRepeat: 'no-repeat',
                          backgroundSize: 'cover',
                          color: item.theme === 'light' ? 'black' : 'white'
                        }
                  }
                  data-card-slide-id={id}
                  aria-hidden={isSlidingOut || undefined}
                >
                  {item.isSkeleton ? (
                    <DeliveryBoxSkeleton />
                  ) : (
                    <DeliveryBox
                      contentClassName={item.available ? deliveryCardFadeIn : undefined}
                      minSpend={minDeliveryThreshold}
                      {...item}
                      loading={showDeliveryButtonLoaders || item.loading}
                    />
                  )}
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Container>
    </Layout>
  )
}

export default observer(SelectDelivery)
